/**
 * RequireAuth guard (CONTRACTS.md §2).
 *
 * - status 'loading' → real blocking session screen (Skeleton), never a flash
 *   of private or public content.
 * - unauthenticated → redirect to the role-correct login, carrying the return
 *   path in location state so login can send the user back.
 * - authenticated with the wrong role → redirect to the user's own portal.
 */
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Skeleton } from '@/components/ui'
import { PATHS } from './paths'
import { useAuth } from '@/features/auth/useAuth'
import type { PortalRole } from '@/features/auth/authContext'

export type AuthStateLocation = {
  from?: string
}

/** Full-screen blocking state while the session is being restored. */
export function SessionLoadingScreen({ label = 'Je sessie wordt geladen…' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '16px',
        padding: '32px 24px',
        maxWidth: 'var(--content-max-width)',
        margin: '0 auto',
        width: '100%',
      }}
    >
      <span className="visually-hidden">{label}</span>
      <Skeleton lines={1} height={28} />
      <Skeleton lines={3} height={18} />
      <Skeleton lines={1} height={48} />
    </div>
  )
}

/** Where a user with this server-side role belongs. */
export function portalHomeForRole(role: 'client' | 'caregiver' | 'admin'): string {
  return role === 'caregiver' ? PATHS.caregiver.dashboard : PATHS.app.today
}

export function RequireAuth({ role, children }: { role?: PortalRole; children: ReactNode }) {
  const { status, role: actualRole } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <SessionLoadingScreen />
  }

  if (status === 'unauthenticated') {
    const loginPath = role === 'caregiver' ? PATHS.auth.caregiverLogin : PATHS.auth.clientLogin
    const from = `${location.pathname}${location.search}`
    return <Navigate to={loginPath} replace state={{ from } satisfies AuthStateLocation} />
  }

  // Role enforcement: server-side profile role decides, never the URL alone.
  if (role && actualRole && actualRole !== role) {
    return <Navigate to={portalHomeForRole(actualRole)} replace />
  }

  return <>{children}</>
}
